import { Stack } from '@mui/material';
import React from 'react';
import { Control, FieldPath, UseFormSetValue } from 'react-hook-form';

import ControlledTextField from '../../../components/TextField/ControlledTextField';
import { maskNumbers, preventNonNumbers } from '../../../utils/helpers';
import ExpirationDateField from '../components/ExpirationDateField';
import { CardTypeFormValues } from '../validation';

interface CardFormProps {
  control: Control<CardTypeFormValues>;
  setCNumber: (ccNumber: string) => void;
  setRhfValue: UseFormSetValue<CardTypeFormValues>;
  showNicknameField?: boolean;
  editing?: boolean;
}

const CardForm = ({
  control,
  setCNumber,
  setRhfValue,
  showNicknameField,
  editing,
}: CardFormProps) => {
  const handleChange =
    (fieldName: string) =>
    (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    ) =>
      preventNonNumbers(e.target.value, (val: string) => {
        onChange(e);
        fieldName === 'cardNumber' && setCNumber(e.target.value);
      });

  const handleBlur =
    (fieldName: string) =>
    (
      e: React.FocusEvent<HTMLInputElement>,
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => void,
    ) => {
      if (!e.target.value.includes(String.fromCharCode(8226))) {
        setCNumber(e.target.value);
      }

      if (e.target.value.length >= 15) {
        maskNumbers(e.target.value, (val: string) => {
          setRhfValue(fieldName as FieldPath<CardTypeFormValues>, val);
          onBlur(e);
        });
      } else {
        onBlur(e);
      }
    };

  const handleFocus =
    (fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      setRhfValue(fieldName as FieldPath<CardTypeFormValues>, '');
    };

  return (
    <Stack spacing={2} direction="column" sx={{
      pb: { mobile: 2, tablet: 3 }
    }}>
      <ControlledTextField
        name="name"
        control={control}
        className="csaa-qm-block"
        label="Name on card"
        autoComplete="cc-name"
        showClearIfValue
        handleClear={() => setRhfValue('name', '')}
        InputProps={{ required: true }}
      />
      <ControlledTextField
        control={control}
        className="csaa-qm-block"
        name="cardNumber"
        disabled={editing}
        label="Card number"
        autoComplete="cc-number"
        inputProps={{
          maxLength: 16,
          // pattern: '([0-9]|•)*',
        }}
        type="text"
        handleBlur={handleBlur('cardNumber')}
        handleChange={handleChange('cardNumber')}
        InputProps={{
          onFocus: handleFocus('cardNumber'),
          required: true,
        }}
        showClearIfValue
        handleClear={() => setRhfValue('cardNumber', '')}
      />
      <ControlledTextField
        control={control}
        className="csaa-qm-block"
        name="expirationDate"
        label="Expiration date"
        autoComplete="cc-exp"
        type="text"
        inputProps={{
          maxLength: 5,
        }}
        InputProps={{
          inputComponent: ExpirationDateField,
          required: true,
        }}
        showClearIfValue
        handleClear={() => setRhfValue('expirationDate', '')}
      />
      <ControlledTextField
        control={control}
        className="csaa-qm-block"
        name="zipCode"
        label="Zip code"
        autoComplete="postal-code"
        inputProps={{ maxLength: 5, pattern: '[0-9]*' }}
        handleChange={handleChange('zipCode')}
        showClearIfValue
        handleClear={() => setRhfValue('zipCode', '')}
        InputProps={{ required: true }}
      />
      {showNicknameField && (
        <ControlledTextField
          control={control}
          className="csaa-qm-block"
          name="nickname"
          label={
            <span>
              Nickname <i>Optional</i>
            </span>
          }
          optional
          showClearIfValue
          handleClear={() => setRhfValue('nickname', '')}
        />
      )}
    </Stack>
  );
};

export default CardForm;
